import React from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Button,
  Stack,
  Avatar,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import GoogleIcon from "@mui/icons-material/Google";
import LogoutIcon from "@mui/icons-material/Logout";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { SiLine } from "react-icons/si";

import { openLogin } from "../api/auth";
import { useAuthStore } from "../store/useAuthStore";
import ModalLoginMailForm from "../components/modals/ModalLoginMail";

interface AccountDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const LoginDrawer: React.FC<AccountDrawerProps> = ({
  open,
  onClose,
}) => {
  const { user, isAuthenticated, logout } = useAuthStore();
  const [loginMail, setLoginMail] = React.useState<boolean>(false);
  const LineIcon = SiLine as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

  const handleGoogleAuth = () => openLogin();
  const handleLogout = () => {
    logout();
    onClose();
  };
  const handleLoginMailClose = () => setLoginMail(false);

  const renderAuthenticatedContent = () => (
    <Stack spacing={3} alignItems="center">
      <Avatar
        src={user?.picture}
        sx={{ width: 80, height: 80, fontSize: "2rem" }}
      >
        {user?.name?.charAt(0).toUpperCase()}
      </Avatar>

      <Box textAlign="center">
        <Typography variant="h6" fontWeight={600}>
          {user?.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.email}
        </Typography>
      </Box>

      <Button
        variant="outlined"
        startIcon={<LogoutIcon />}
        onClick={handleLogout}
        fullWidth
        sx={{
          borderRadius: 2,
          maxWidth: 220,
          color: "text.primary",
          borderColor: "grey.300",
          "&:hover": { backgroundColor: "grey.100", borderColor: "grey.400" },
        }}
      >
        Logout
      </Button>
    </Stack>
  );

  const renderUnauthenticatedContent = () => (
    <Stack spacing={2} width="100%">
      <Typography variant="h6" fontWeight={700} textAlign="center">
        Welcome to Tourist eSIM
      </Typography>

      <Button
        variant="contained"
        startIcon={<GoogleIcon />}
        onClick={handleGoogleAuth}
        fullWidth
        sx={{ borderRadius: 2, py: 1.5, textTransform: "none" }}
      >
        Continue with Google
      </Button>

      <Button
        variant="outlined"
        startIcon={<MailOutlineIcon />}
        onClick={() => setLoginMail(true)}
        fullWidth
        sx={{
          borderRadius: 2,
          py: 1.5,
          textTransform: "none",
          color: "text.primary",
          borderColor: "grey.300",
          "&:hover": { backgroundColor: "grey.100", borderColor: "grey.400" },
        }}
      >
        Continue with Email
      </Button>

      <Button
        variant="outlined"
        startIcon={<LineIcon color="#00B900" />}
        onClick={() =>
          (window.location.href = "https://esim-api.laotel.com/api/auth/line")
        }
        fullWidth
        sx={{
          borderRadius: 2,
          py: 1.5,
          textTransform: "none",
          color: "#00B900",
          borderColor: "#00B900",
          "&:hover": { backgroundColor: "#e8f5e9", borderColor: "#00B900" },
        }}
      >
        Continue with Line
      </Button>
    </Stack>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 400 },
          height: "100vh", // full height
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Close button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flexGrow: 1, // take remaining space
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
        }}
      >
        {isAuthenticated
          ? renderAuthenticatedContent()
          : renderUnauthenticatedContent()}
      </Box>

      <ModalLoginMailForm open={loginMail} onClose={handleLoginMailClose} />
    </Drawer>
  );
};
